import React, { useState } from 'react';
import { useApiClient } from '@m43/core';
import { useMutation } from '@tanstack/react-query';
import { useForm, UseFormReturn, useWatch } from 'react-hook-form';
import { useFullscreenModalControls } from '../modals/context/modal-context-provider';
import {
    BlankModal,
    ButtonGroup,
    getLocalTimeZone,
    ModalBody,
    ModalFooter,
    ModalHeader,
    VStack,
    now,
    FormRow,
} from '@cad-arc';
import { FormBody } from '../forms/integrated-form-components/form-body';
import { FormCancelButton } from '../forms/integrated-form-components/form-cancel-button';
import { FormSaveButton } from '../forms/integrated-form-components/form-save-button';
import { IntegratedFormProvider } from '../forms/integrated-form-provider';
import {
    EventPanelForm,
    EventPanelFormType,
    EventPanelOptions,
    EventPanelResource,
} from '@mark43/cad-api';
import { FormDateInput } from '../forms/integrated-form-controls/form-date-input';
import { FormSelect } from '../forms/integrated-form-controls/form-select';
import { RefreshEventPanel } from './refresh-event-panel';
import { WorkspacePanel } from '../panels/workspace-panel';
import { filterInvalidEventPanelEntities } from './event-panel-form/filter-invalid-event-panel-entities';
import { addDays } from 'date-fns';

export type ScheduleEventModalComponentType = typeof ScheduleEventModal;
type ScheduleEventForm = Pick<
    EventPanelFormType,
    'scheduledEventStartDateTime' | 'scheduledEventFrequency' | 'scheduledEventEndDateTime'
>;

const startDateValidationRule = {
    validate: (value: string) => {
        if (!value) {
            return 'Pick a future time';
        }
        const valueAsUnixTimestamp = new Date(value).getTime();
        if (valueAsUnixTimestamp < Date.now()) {
            return 'Pick a future time';
        }
        return true;
    },
};

const requiredRule = { required: 'Required' };

export function ScheduleEventModal(props: {
    form: UseFormReturn<EventPanelFormType>;
    eventPanelOptions: EventPanelOptions;
    refreshEventPanel: RefreshEventPanel;
    workspacePanel: WorkspacePanel;
    onSuccess?: () => void;
}): React.ReactElement {
    const apiClient = useApiClient();

    const modalControls = useFullscreenModalControls();

    const initialFormValues = props.form.getValues();

    const scheduleEventForm = useForm<ScheduleEventForm>({
        defaultValues: {
            scheduledEventStartDateTime:
                initialFormValues.scheduledEventStartDateTime ??
                addDays(new Date(), 1).toISOString(),
            scheduledEventFrequency: initialFormValues.scheduledEventFrequency,
            scheduledEventEndDateTime: initialFormValues.scheduledEventEndDateTime,
        },
        mode: 'onBlur',
    });

    const scheduleEventMutation = useMutation(
        (eventPanelFormValues: EventPanelFormType) => {
            return apiClient.request<EventPanelResource.SaveEventPanel>({
                method: 'PUT',
                url: 'event_panel',
                data: eventPanelFormValues as EventPanelForm,
            });
        },
        {
            onSuccess(data) {
                const eventPanelViews = data.data.data?.eventPanelViews;
                if (eventPanelViews) {
                    const [eventPanelView, ..._eventPanelViewsForOtherEvents] = eventPanelViews;

                    props.onSuccess?.();
                    props.refreshEventPanel({ eventPanelView });
                    modalControls.close();
                }
            },
        }
    );

    const minimumDate = React.useMemo(() => {
        return now(getLocalTimeZone());
    }, []);

    React.useEffect(() => {
        const defaultFrequencyOption = props.eventPanelOptions.scheduledEventFrequencyOptions.find(
            (option) => option.isDefault
        );
        if (defaultFrequencyOption && !props.workspacePanel.linkedEntityId) {
            scheduleEventForm.setValue('scheduledEventFrequency', defaultFrequencyOption);
        }
    }, [props.eventPanelOptions, props.workspacePanel.linkedEntityId, scheduleEventForm]);

    const onSubmit = React.useCallback(
        (scheduleEventFormValues: ScheduleEventForm) => {
            const eventFormValues = props.form.getValues();
            const mergedValues = {
                ...eventFormValues,
                ...scheduleEventFormValues,
            };
            if (mergedValues.scheduledEventFrequency?.isDefault) {
                mergedValues.scheduledEventEndDateTime = null;
            }
            scheduleEventMutation.mutate(filterInvalidEventPanelEntities(mergedValues));
        },
        [props.form, scheduleEventMutation]
    );

    const scheduledEventFrequency = useWatch({
        control: scheduleEventForm.control,
        name: 'scheduledEventFrequency',
    });

    const showEndDateTime = scheduledEventFrequency != null && !scheduledEventFrequency.isDefault;

    const form = props.form;
    const endDateValidationRule = React.useMemo(
        () => ({
            validate: (value: string) => {
                if (!value) {
                    return 'Pick a future time';
                }
                const valueAsUnixTimestamp = new Date(value).getTime();
                if (valueAsUnixTimestamp < Date.now()) {
                    return 'Pick a future time';
                }
                const startDateTime = form.getValues().scheduledEventStartDateTime;
                if (startDateTime) {
                    const startDateTiemAsUnixTimeStamp = new Date(startDateTime).getTime();
                    if (valueAsUnixTimestamp <= startDateTiemAsUnixTimeStamp) {
                        return 'Must be after Start Date and Time';
                    }
                }
                return true;
            },
        }),
        [form]
    );
    const [portalElement, setPortalElement] = useState<HTMLDivElement | null>(null);
    return (
        <IntegratedFormProvider
            mutation={scheduleEventMutation}
            form={scheduleEventForm}
            onSubmit={onSubmit}
        >
            <BlankModal isOpen={true} onClose={() => modalControls.close()}>
                <ModalHeader>Schedule Event</ModalHeader>
                <ModalBody ref={setPortalElement}>
                    <FormBody>
                        <VStack w="100%">
                            <FormRow>
                                <FormDateInput
                                    name="scheduledEventStartDateTime"
                                    label="Start Date and Time"
                                    control={scheduleEventForm.control}
                                    rules={startDateValidationRule}
                                    length="md"
                                    granularity="minute"
                                    hasCalendar={true}
                                    minValue={minimumDate}
                                    hideTimeZone={true}
                                    portalElement={portalElement}
                                />
                            </FormRow>
                            <FormRow>
                                <FormSelect
                                    name="scheduledEventFrequency"
                                    label="Frequency"
                                    options={props.eventPanelOptions.scheduledEventFrequencyOptions}
                                    control={scheduleEventForm.control}
                                    rules={requiredRule}
                                    isClearable={false}
                                    length="md"
                                />
                            </FormRow>
                            {showEndDateTime ? (
                                <FormRow>
                                    <FormDateInput
                                        name="scheduledEventEndDateTime"
                                        label="End Date and Time"
                                        control={scheduleEventForm.control}
                                        rules={endDateValidationRule}
                                        length="md"
                                        granularity="minute"
                                        hasCalendar={true}
                                        minValue={minimumDate}
                                        hideTimeZone={true}
                                        portalElement={portalElement}
                                    />
                                </FormRow>
                            ) : undefined}
                        </VStack>
                    </FormBody>
                </ModalBody>
                <ModalFooter>
                    <ButtonGroup>
                        <FormCancelButton
                            onClick={() => {
                                modalControls.close();
                            }}
                        />
                        <FormSaveButton>Schedule</FormSaveButton>
                    </ButtonGroup>
                </ModalFooter>
            </BlankModal>
        </IntegratedFormProvider>
    );
}
